import { useState } from "react";
import type { ReactNode } from "react";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { formatNavCount } from "./navCount";

/**
 * Every string this component renders. Passed in rather than translated here:
 * the library has no catalogue, and both portals name these differently in
 * their own (`nav.collapse` in one, `seller.navCollapse` in the other).
 */
export type SidebarLabels = {
  appName: string;
  collapse: string;
  hintSetup: string;
  storeLive: string;
  storeSettings: string;
  switchStore: string;
  currentStore: string;
};

/**
 * A routed anchor. Apps wire their own router here — the library must not
 * depend on one, the same contract `BottomNav` already uses.
 */
export type SidebarRenderLink = (props: {
  href: string;
  className?: string;
  children: ReactNode;
  "aria-label"?: string;
  "aria-current"?: "page";
  /** Stable styling hooks a dashboard theme stylesheet targets (see the
   *  restaurant's `app/themes/`). Spread through to the rendered link. */
  "data-ui"?: string;
  "data-active"?: "";
}) => ReactNode;

/** A store the seller can switch to (subset of the session's availableStores). */
export type SidebarStore = {
  storeId: string;
  storeSlug: string;
  storeName: string;
  storeShort: string;
};

// ─────────────────────────────────────────────────────────────────────────────
// Desktop vertical sidebar (lg+). RTL-first: pinned to the inline-start edge
// (= right in Arabic) via logical properties. Light surface (Trioty redesign).
// The collapse control fully hides the sidebar (AppShell unmounts it and shows a
// toggle in the top bar to bring it back) — there is no icon-rail state.
// Presentation-only — all data/handlers come from AppShell. Structure:
// logo + hide → store card/switcher → grouped nav → account.
// ─────────────────────────────────────────────────────────────────────────────

export type SidebarSubItem = {
  href: string;
  label: string;
  isActive: boolean;
  /**
   * Glyph for the row. A bullet told the reader only "this is nested", which
   * the indent and the rail already say; an icon is the thing that makes
   * "Wallet" and "Wallet transactions" tell themselves apart at a glance.
   * Falls back to the bullet when a caller has no icon for a row.
   */
  icon?: ReactNode;
  /**
   * A small figure at the row's end (e.g. how many dishes, how many items run
   * low). Omitted, or `null`, draws nothing — a count of nothing is not shown.
   */
  count?: number | null;
};

/**
 * A nav row. Exactly one of `href` / `onClick` is set:
 *  - `href` → a destination; renders as a `<Link>` (`<a>`).
 *  - `onClick` → an in-place action with no route (e.g. "Share store", which
 *    opens the share sheet); renders as a `<button>`.
 * An action row is never `isActive` — there is no URL for it to match.
 */
export type SidebarNavItem = {
  /** Route target. Omit for action rows (see `onClick`). */
  href?: string;
  /** Action handler for route-less rows. Omit for destinations (see `href`). */
  onClick?: () => void;
  label: string;
  icon: ReactNode;
  isActive: boolean;
  /** Numeric badge (e.g. actionable orders). */
  count?: number;
  /**
   * The badge's ink. "info" (default) is the soft blue tint; "accent" is the
   * near-black primary with `--on-accent` — the seller's tier-1 "بانتظار
   * قرارك" count, which the phone's bottom-bar badge already draws so.
   */
  countTone?: "info" | "accent";
  /** Small setup hint dot. */
  showHint?: boolean;
  /** When present, the row becomes an expandable group with a nested list. */
  subItems?: SidebarSubItem[];
  /**
   * Whether an expandable group starts open. Defaults to `isActive` — open
   * only while one of its pages is current, the seller's behaviour. A sidebar
   * made ENTIRELY of groups (the restaurant's sectioned nav) opens them all,
   * or the first load shows five closed headings and no destinations.
   */
  defaultOpen?: boolean;
};

export type SidebarGroup = {
  /** Omitted for the primary (un-headed) tier. */
  header?: string;
  items: SidebarNavItem[];
};

export type SidebarIdentity = {
  storeName: string;
  storeShort: string | null;
  initial: string;
  /** Uploaded store logo; falls back to the monogram initial when null. */
  logoUrl: string | null;
};

type SidebarProps = {
  dashboardHref: string;
  settingsHref: string;
  groups: SidebarGroup[];
  /** When true, the sidebar animates its width to 0 (fully hidden). */
  hidden: boolean;
  /** Hide the whole sidebar (header collapse button). */
  onHide: () => void;
  identity: SidebarIdentity;
  /** Stores the seller can switch between (from the session). */
  availableStores: SidebarStore[];
  /** Currently-active store slug. */
  activeStoreSlug: string;
  /** Switch to another store (wraps `useStoreSwitch`). */
  onSwitchStore: (slug: string) => void;
  /** True while a switch is in flight. */
  isSwitchingStore: boolean;
  /** Account menu (user-row trigger + dropdown), rendered in the footer. */
  accountSlot: ReactNode;
  /** Every string this renders — the library has no catalogue. */
  labels: SidebarLabels;
  /** Routed anchors. The library must not depend on a router. */
  renderLink: SidebarRenderLink;
  /** The app's mark, shown top-start and linking to `dashboardHref`. */
  logo: ReactNode;
  /**
   * Where the store identity ITSELF leads, when it is the app's way home.
   *
   * Unset (the seller portal), the identity is the store-settings card, or the
   * switcher's trigger when there are several stores. Set, the store's tile and
   * name are a link here; with several stores the switcher keeps its caret as
   * a button of its own, so neither job is lost.
   */
  homeHref?: string;
  /** The hide control's glyph. Defaults to the library's panel mark. */
  collapseIcon?: ReactNode;
  /**
   * The portal locale, for the digits of nav counts (Arabic-Indic in `ar`).
   * Unset, `<html lang>` is used.
   */
  locale?: string;
};

export function Sidebar({
  dashboardHref,
  settingsHref,
  groups,
  hidden,
  onHide,
  identity,
  availableStores,
  activeStoreSlug,
  onSwitchStore,
  isSwitchingStore,
  accountSlot,
  labels,
  renderLink,
  logo,
  homeHref,
  collapseIcon,
  locale,
}: SidebarProps) {
  const appName = labels.appName;

  return (
    <aside
      data-ui="sidebar"
      aria-hidden={hidden || undefined}
      className={[
        "hidden h-full shrink-0 overflow-hidden bg-[color:var(--sidebar-surface)] transition-[width] duration-200 ease-out lg:block",
        hidden ? "border-e-0" : "border-e-[length:var(--border-card-width,1px)] border-[color:var(--sidebar-border)]",
      ].join(" ")}
      style={{ width: hidden ? 0 : "var(--sidebar-width, 264px)" }}
    >
      <div className="flex h-full w-[var(--sidebar-width,264px)] flex-col overflow-hidden text-[color:var(--sidebar-ink)]">
      {/* Logo + hide toggle */}
      <div data-ui="sidebar-brand" className="flex h-16 shrink-0 items-center justify-between px-4">
        {renderLink({
          href: dashboardHref,
          "aria-label": appName,
          className: "flex items-center",
          // The mark itself is the app's, not the library's — a portal may ship
          // a wordmark, a monogram or a theme-aware pair, and none of that is
          // this component's business.
          children: logo,
        })}
        <button
          type="button"
          onClick={onHide}
          aria-label={labels.collapse}
          className="inline-flex h-8 w-8 items-center justify-center rounded-[8px] text-[color:var(--sidebar-ink-disabled)] transition-colors hover:bg-[color:var(--sidebar-hover-bg)] hover:text-[color:var(--sidebar-ink)]"
        >
          {collapseIcon ?? <CollapseIcon />}
        </button>
      </div>

      {/* Store identity card — a switcher when the seller has >1 store,
          otherwise the Link-to-settings card. */}
      <div data-ui="sidebar-identity" className="px-3">
        {availableStores.length > 1 ? (
          <StoreSwitcher
            identity={identity}
            settingsHref={settingsHref}
            homeHref={homeHref}
            availableStores={availableStores}
            activeStoreSlug={activeStoreSlug}
            onSwitchStore={onSwitchStore}
            isSwitchingStore={isSwitchingStore}
            labels={labels}
            renderLink={renderLink}
          />
        ) : (
          <StoreIdentityCard
            identity={identity}
            settingsHref={homeHref ?? settingsHref}
            labels={labels}
            renderLink={renderLink}
          />
        )}
      </div>

      {/* Nav (scroll region) */}
      <nav data-ui="sidebar-nav" className="no-scrollbar mt-2 flex-1 overflow-y-auto px-3 pb-2">
        {groups.map((group, gi) => (
          <div key={group.header ?? `primary-${gi}`} data-ui="sidebar-group" className={gi > 0 ? "mt-4" : ""}>
            {group.header ? (
              <div data-ui="sidebar-group-label" className="ui-label flex items-center gap-2 px-3 pb-1.5 pt-1 text-[11px] text-[color:var(--sidebar-ink-disabled)]">
                <span
                  aria-hidden="true"
                  data-ui="sidebar-group-dot"
                  className="inline-block h-[13px] w-[13px] rounded-full border-[1.5px] border-dashed border-[color:var(--border-strong)]"
                />
                {group.header}
              </div>
            ) : null}
            <ul className="flex flex-col gap-0.5">
              {group.items.map((item) => (
                <li key={item.href ?? item.label}>
                  {item.subItems ? (
                    <NavGroup item={item} renderLink={renderLink} locale={locale} />
                  ) : (
                    <NavRow
                      item={item}
                      hintLabel={labels.hintSetup}
                      renderLink={renderLink}
                      locale={locale}
                    />
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      {/* The setup checklist now lives on the dashboard (DashboardSetupGuide),
          so the sidebar no longer duplicates it here. */}

      {/* Account footer */}
      <div data-ui="sidebar-footer" className="shrink-0 border-t border-[color:var(--sidebar-border)] [border-top-style:var(--divider-style,solid)] p-2">
        {accountSlot}
      </div>
      </div>
    </aside>
  );
}

// ─── Nav row ──────────────────────────────────────────────────────────────────

function NavRow({
  renderLink,
  item,
  hintLabel,
  locale,
}: {
  item: SidebarNavItem;
  hintLabel: string;
  renderLink: SidebarRenderLink;
  locale?: string;
}) {
  const className = [
    "relative flex min-h-[40px] w-full items-center gap-3 rounded-[var(--radius-nav,10px)] px-3 text-[13.5px] transition-colors",
    item.isActive
      ? "bg-[color:var(--sidebar-active-bg)] font-semibold text-[color:var(--sidebar-active-ink)] shadow-[var(--shadow-nav-active,inset_0_0_0_1px_var(--pillring))]"
      : "font-medium text-[color:var(--sidebar-ink-muted)] hover:bg-[color:var(--sidebar-hover-bg)] hover:text-[color:var(--sidebar-ink)]",
  ].join(" ");

  const content = (
    <>
      <span data-ui="sidebar-row-icon" className="inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center">
        {item.icon}
      </span>
      <span data-ui="sidebar-row-label" className="min-w-0 flex-1 truncate text-start">{item.label}</span>
      {item.count !== undefined ? (
        <span
          data-ui="sidebar-row-count"
          className={[
            "inline-flex min-w-[20px] items-center justify-center rounded-full px-1.5 py-0.5 text-[11px] font-bold leading-none",
            item.countTone === "accent"
              ? "bg-[color:var(--accent-500)] text-[color:var(--on-accent)]"
              : "bg-[color:var(--info-bg)] text-[color:var(--info-fg)]",
          ].join(" ")}
        >
          {formatNavCount(item.count, locale)}
        </span>
      ) : item.showHint ? (
        <span
          aria-label={hintLabel}
          className="h-1.5 w-1.5 shrink-0 rounded-full bg-[color:var(--gold-500)]"
        />
      ) : null}
    </>
  );

  // Route-less rows (e.g. "Share store") are actions, so they must be a
  // <button> — an <a> with no href is not focusable and lies to screen readers.
  if (item.href === undefined) {
    return (
      <button
        type="button"
        onClick={item.onClick}
        data-ui="sidebar-row"
        data-active={item.isActive ? "" : undefined}
        className={className}
      >
        {content}
      </button>
    );
  }

  return renderLink({
    href: item.href,
    "aria-current": item.isActive ? "page" : undefined,
    "data-ui": "sidebar-row",
    "data-active": item.isActive ? "" : undefined,
    className,
    children: content,
  });
}

// ─── Expandable nav group (Store settings → sections) ─────────────────────────

function NavGroup({
  item,
  renderLink,
  locale,
}: {
  item: SidebarNavItem;
  renderLink: SidebarRenderLink;
  locale?: string;
}) {
  const [open, setOpen] = useState(item.defaultOpen ?? item.isActive);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-ui="sidebar-section"
        className={[
          "relative flex min-h-[40px] w-full items-center gap-3 rounded-[var(--radius-nav,10px)] px-3 text-[13.5px] transition-colors",
          item.isActive
            ? "bg-[color:var(--sidebar-active-bg)] font-semibold text-[color:var(--sidebar-active-ink)] shadow-[var(--shadow-nav-active,inset_0_0_0_1px_var(--pillring))]"
            : "font-medium text-[color:var(--sidebar-ink-muted)] hover:bg-[color:var(--sidebar-hover-bg)] hover:text-[color:var(--sidebar-ink)]",
        ].join(" ")}
      >
        <span data-ui="sidebar-section-icon" className="inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center">
          {item.icon}
        </span>
        <span className="min-w-0 flex-1 truncate text-start">{item.label}</span>
        <svg
          data-ui="sidebar-section-chevron"
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className={`shrink-0 text-[color:var(--sidebar-ink-disabled)] transition-transform ${open ? "rotate-180" : ""}`}
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {open && item.subItems ? (
        <div data-ui="sidebar-subrows" className="my-0.5 ms-[21px] flex flex-col gap-0.5 border-s-[1.5px] border-[color:var(--line2)] ps-3">
          {item.subItems.map((sub) => (
            <SubRow
              key={sub.href}
              href={sub.href}
              renderLink={renderLink}
              isActive={sub.isActive}
              className={[
                "flex items-center gap-2.5 rounded-[9px] px-[11px] py-2 text-[13px] transition-colors",
                sub.isActive
                  ? "bg-[color:var(--sidebar-active-bg)] font-bold text-[color:var(--sidebar-active-ink)]"
                  : "font-medium text-[color:var(--sidebar-ink-muted)] hover:bg-[color:var(--sidebar-hover-bg)] hover:text-[color:var(--sidebar-ink)]",
              ].join(" ")}
            >
              {sub.icon ? (
                <span
                  aria-hidden="true"
                  data-ui="sidebar-subrow-icon"
                  className="flex h-4 w-4 shrink-0 items-center justify-center [&>svg]:h-4 [&>svg]:w-4"
                >
                  {sub.icon}
                </span>
              ) : (
                <span
                  aria-hidden="true"
                  className="h-[5px] w-[5px] shrink-0 rounded-full"
                  style={{ background: sub.isActive ? "var(--accent-500)" : "var(--sidebar-ink-disabled)" }}
                />
              )}
              {sub.label}
              {sub.count != null ? (
                <span
                  data-ui="sidebar-subrow-count"
                  className="ms-auto shrink-0 text-[12px] tabular-nums text-[color:var(--sidebar-ink-disabled)]"
                >
                  {formatNavCount(sub.count, locale)}
                </span>
              ) : null}
            </SubRow>
          ))}
        </div>
      ) : null}
    </>
  );
}

// ─── Store identity card ──────────────────────────────────────────────────────

/** Store logo (or gradient monogram fallback) — shared by the card & switcher. */
/** A nested destination row. Exists only so the `renderLink` contract can wrap
 *  arbitrary children without every call site rebuilding the props object. */
function SubRow({
  href,
  className,
  isActive,
  renderLink,
  children,
}: {
  href: string;
  className: string;
  isActive: boolean;
  renderLink: SidebarRenderLink;
  children: ReactNode;
}) {
  return renderLink({
    href,
    className,
    "aria-current": isActive ? "page" : undefined,
    "data-ui": "sidebar-subrow",
    "data-active": isActive ? "" : undefined,
    children,
  });
}

function StoreMonogram({
  logoUrl,
  storeName,
  initial,
}: {
  logoUrl: string | null;
  storeName: string;
  initial: string;
}) {
  return logoUrl ? (
    <img
      data-ui="sidebar-identity-tile"
      src={logoUrl}
      alt={storeName}
      className="h-8 w-8 shrink-0 rounded-[var(--radius-tile,9px)] bg-white object-cover"
    />
  ) : (
    <div
      aria-hidden="true"
      data-ui="sidebar-identity-tile"
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-tile,9px)] text-[12px] font-extrabold text-white"
      style={{ background: "linear-gradient(135deg, #1f2c4d, #2f4170)" }}
    >
      {initial}
    </div>
  );
}

/** Green "Live" dot with a pulsing ring. */
function LiveDot() {
  return (
    <span aria-hidden="true" className="relative inline-block h-1.5 w-1.5">
      <span className="absolute inset-0 rounded-full bg-[#16A34A]" />
      <span className="live-ping absolute inset-0 rounded-full bg-[#16A34A]" />
    </span>
  );
}

/** Identity content (monogram + name + short + Live) shared by the settings
 * card and the switcher trigger. `trailing` renders the switcher caret. */
function StoreIdentityBody({
  identity,
  trailing,
  labels,
}: {
  identity: SidebarIdentity;
  trailing?: ReactNode;
  labels: SidebarLabels;
}) {
  const { storeName, storeShort, initial, logoUrl } = identity;
  return (
    <div className="flex items-center gap-2.5">
      <StoreMonogram logoUrl={logoUrl} storeName={storeName} initial={initial} />
      <div className="min-w-0 flex-1">
        <div data-ui="sidebar-identity-name" className="ui-display truncate text-[13px] leading-tight tracking-normal text-[color:var(--sidebar-ink)]">
          {storeName}
        </div>
        <div data-ui="sidebar-identity-status" className="mt-0.5 flex items-center gap-1.5 text-[10.5px] font-medium text-[color:var(--sidebar-ink-disabled)]">
          {storeShort ? (
            <span className="font-mono tracking-[0.04em]">{storeShort}</span>
          ) : null}
          <span aria-hidden="true" data-ui="sidebar-identity-sep" className="text-[color:var(--border-strong)]">
            •
          </span>
          <span data-ui="sidebar-identity-live" className="inline-flex items-center gap-1 font-semibold text-[color:var(--success-500)]">
            <LiveDot />
            {labels.storeLive}
          </span>
        </div>
      </div>
      {trailing}
    </div>
  );
}

function StoreIdentityCard({
  identity,
  settingsHref,
  labels,
  renderLink,
}: {
  identity: SidebarIdentity;
  settingsHref: string;
  labels: SidebarLabels;
  renderLink: SidebarRenderLink;
}) {

  return renderLink({
    href: settingsHref,
    "aria-label": labels.storeSettings,
    "data-ui": "sidebar-identity-card",
    className:
      "block rounded-[var(--radius-panel,14px)] border-[length:var(--border-card-width,1px)] border-[color:var(--sidebar-border)] bg-[color:var(--sidebar-surface-elevated)] px-3 py-2.5 transition-colors hover:border-[color:var(--border-strong)]",
    children: <StoreIdentityBody identity={identity} labels={labels} />,
  });
}

// ─── Store switcher (multi-store) ─────────────────────────────────────────────

function StoreSwitcher({
  identity,
  settingsHref,
  homeHref,
  availableStores,
  activeStoreSlug,
  onSwitchStore,
  isSwitchingStore,
  labels,
  renderLink,
}: {
  identity: SidebarIdentity;
  settingsHref: string;
  homeHref?: string;
  availableStores: SidebarStore[];
  activeStoreSlug: string;
  onSwitchStore: (slug: string) => void;
  isSwitchingStore: boolean;
  labels: SidebarLabels;
  renderLink: SidebarRenderLink;
}) {
  const switchLabel = labels.switchStore;

  return (
    <Menu as="div" className="relative">
      {({ open }) => (
        <>
          {homeHref ? (
            /* The identity is the way home; the caret alone switches stores. */
            <div data-ui="sidebar-identity-card" className="flex items-center gap-1">
              {renderLink({
                href: homeHref,
                "aria-label": identity.storeName,
                "data-ui": "sidebar-identity-home",
                className: "min-w-0 flex-1",
                children: <StoreIdentityBody identity={identity} labels={labels} />,
              })}
              <MenuButton
                aria-label={switchLabel}
                disabled={isSwitchingStore}
                data-ui="sidebar-identity-switch"
                className={[
                  "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-[var(--radius-control,8px)] hover:bg-[color:var(--sidebar-hover-bg)] focus:outline-none",
                  isSwitchingStore ? "cursor-wait opacity-70" : "",
                ].join(" ")}
              >
                <SwitcherCaret open={open} />
              </MenuButton>
            </div>
          ) : (
          <MenuButton
            aria-label={switchLabel}
            disabled={isSwitchingStore}
            data-ui="sidebar-identity-card"
            className={[
              "block w-full rounded-[var(--radius-panel,14px)] border-[length:var(--border-card-width,1px)] px-3 py-2.5 text-start transition-colors focus:outline-none",
              "border-[color:var(--sidebar-border)] bg-[color:var(--sidebar-surface-elevated)]",
              "hover:border-[color:var(--border-strong)]",
              open ? "border-[color:var(--border-strong)]" : "",
              isSwitchingStore ? "cursor-wait opacity-70" : "",
            ].join(" ")}
          >
            <StoreIdentityBody
              identity={identity}
              labels={labels}
              trailing={<SwitcherCaret open={open} />}
            />
          </MenuButton>
          )}

          <MenuItems
            anchor="bottom start"
            className="z-50 mt-2 w-[248px] origin-top overflow-hidden rounded-[14px] border border-[color:var(--border-light)] bg-[color:var(--surface)] p-1.5 shadow-[0_24px_60px_rgba(15,20,40,0.16),0_4px_12px_rgba(15,20,40,0.06)] focus:outline-none"
          >
            {availableStores.map((store) => {
              const isActive = store.storeSlug === activeStoreSlug;
              return (
                <MenuItem key={store.storeId}>
                  <button
                    type="button"
                    disabled={isSwitchingStore}
                    aria-current={isActive ? "true" : undefined}
                    onClick={() => {
                      if (!isActive) onSwitchStore(store.storeSlug);
                    }}
                    className={[
                      "flex w-full items-center gap-2.5 rounded-[9px] px-2.5 py-2 text-start transition-colors",
                      "data-[focus]:bg-[color:var(--surface-alt)]",
                      isSwitchingStore ? "cursor-wait opacity-60" : "",
                    ].join(" ")}
                  >
                    <span
                      aria-hidden="true"
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] text-[13px] font-bold text-white"
                      style={{
                        background: "linear-gradient(135deg, #1f2c4d, #2f4170)",
                      }}
                    >
                      {(store.storeName || "?").trim().charAt(0).toUpperCase()}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13.5px] font-semibold leading-tight text-[color:var(--text-primary)]">
                        {store.storeName}
                      </span>
                      {store.storeShort ? (
                        <span className="mt-0.5 block truncate font-mono text-[11px] tracking-[0.04em] text-[color:var(--text-muted)]">
                          {store.storeShort}
                        </span>
                      ) : null}
                    </span>
                    {isActive ? (
                      <span className="inline-flex shrink-0 items-center gap-1 text-[11px] font-semibold text-[color:var(--success-500)]">
                        <CheckMarkIcon />
                        {labels.currentStore}
                      </span>
                    ) : null}
                  </button>
                </MenuItem>
              );
            })}

            <div aria-hidden="true" className="my-1 h-px bg-[color:var(--border-light)]" />

            <MenuItem>
              <SubRow
                href={settingsHref}
                renderLink={renderLink}
                isActive={false}
                className="flex w-full items-center gap-2.5 rounded-[9px] px-2.5 py-2 text-start text-[13.5px] font-medium text-[color:var(--text-primary)] transition-colors data-[focus]:bg-[color:var(--surface-alt)]"
              >
                <span
                  aria-hidden="true"
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] bg-[color:var(--surface-alt)] text-[color:var(--text-secondary)]"
                >
                  <SwitcherSettingsIcon />
                </span>
                {labels.storeSettings}
              </SubRow>
            </MenuItem>
          </MenuItems>
        </>
      )}
    </Menu>
  );
}

function SwitcherCaret({ open }: { open: boolean }) {
  return (
    <svg
              data-ui="sidebar-identity-chevron"
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={[
        "shrink-0 text-[color:var(--sidebar-ink-disabled)] transition-transform duration-150",
        open ? "text-[color:var(--sidebar-ink)]" : "",
      ].join(" ")}
    >
      <path d="m8 9 4-4 4 4" />
      <path d="m16 15-4 4-4-4" />
    </svg>
  );
}

function CheckMarkIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.4}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function CollapseIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="3" width="18" height="18" rx="2.5" />
      <path d="M9 3v18" />
    </svg>
  );
}

function SwitcherSettingsIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

// ─── Icons ────────────────────────────────────────────────────────────────────

const ICON = {
  width: 22,
  height: 22,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor" as const,
  strokeWidth: 1.7,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

export function DashboardSidebarIcon() {
  return (
    <svg {...ICON}>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 9h18M9 21V9" />
    </svg>
  );
}
export function OrdersSidebarIcon() {
  return (
    <svg {...ICON}>
      <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" />
      <path d="M3 6h18" />
      <path d="M16 10a4 4 0 0 1-8 0" />
    </svg>
  );
}
export function ProductsSidebarIcon() {
  return (
    <svg {...ICON}>
      <path d="M21 8 12 3 3 8v8l9 5 9-5V8Z" />
      <path d="m3 8 9 5 9-5" />
      <path d="M12 13v8" />
    </svg>
  );
}
export function CategoriesSidebarIcon() {
  return (
    <svg {...ICON}>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
    </svg>
  );
}
export function CouponsSidebarIcon() {
  return (
    <svg {...ICON}>
      <path d="M19 5 5 19" />
      <circle cx="6.5" cy="6.5" r="2.5" />
      <circle cx="17.5" cy="17.5" r="2.5" />
    </svg>
  );
}
export function ReviewsSidebarIcon() {
  return (
    <svg {...ICON}>
      <path d="m12 3 2.9 5.9 6.1.9-4.5 4.3 1 6.1L12 17.8 6.5 20.2l1-6.1L3 9.8l6.1-.9L12 3Z" />
    </svg>
  );
}
export function CheckoutLinksSidebarIcon() {
  return (
    <svg {...ICON}>
      <path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7" />
      <path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7" />
    </svg>
  );
}
export function StoreEditorSidebarIcon() {
  return (
    <svg {...ICON}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z" />
    </svg>
  );
}
export function PaymentSidebarIcon() {
  return (
    <svg {...ICON}>
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <path d="M2 10h20" />
      <path d="M6 15h4" />
    </svg>
  );
}
export function SettingsSidebarIcon() {
  return (
    <svg {...ICON}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}
export function ProfileSidebarIcon() {
  return (
    <svg {...ICON}>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
    </svg>
  );
}

export function TransactionsSidebarIcon() {
  return (
    <svg {...ICON}>
      <rect x="2" y="5" width="20" height="14" rx="2.5" />
      <path d="M2 10h20" />
      <path d="M6 15h3" />
      <path d="M13 15h5" />
    </svg>
  );
}

/** Payments — the seller's own money with the platform. A banknote with a coin
 *  rather than a card, so it does not read as `TransactionsSidebarIcon` (the
 *  gateway payments the store RECEIVES) two rows above it. */
export function PaymentsSidebarIcon() {
  return (
    <svg {...ICON}>
      <rect x="2" y="6" width="20" height="12" rx="2.5" />
      <circle cx="12" cy="12" r="2.5" />
      <path d="M6 12h.01M18 12h.01" />
    </svg>
  );
}

export function ShareSidebarIcon() {
  return (
    <svg {...ICON}>
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4" />
    </svg>
  );
}

export function CustomersSidebarIcon() {
  return (
    <svg {...ICON}>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    </svg>
  );
}
